// src/components/route.jsx
// RoutePlanner - select a destination (city/state/ZIP), filter companies within
// a radius, and have AI plan an optimized driving route through the selected
// stops.
//
// Filtering priority:
//   1. Companies with a ZIP code -- match by ZIP centroid distance
//   2. Companies missing ZIP but have city + state -- match by city centroid
//   3. Companies missing ZIP + city but have state -- shown in a "state-only,
//      approximate" bucket separated from the in-radius matches
//   4. Companies with no city/state/zip -- never shown

import { useState, useEffect, useMemo } from "react";
import { aiCall } from "../helpers.js";
import { Btn, FI, Spin } from "./ui.jsx";
import {
  loadZipDatabase,
  resolveDestination,
  locationForCompany,
  stateForCompany,
  milesBetween,
  US_STATES,
  normalizeState,
} from "../geo.js";

const RADIUS_OPTIONS = [50, 100, 200];

// Company type filter buckets. Each bucket maps to one or more raw HubSpot
// `type` values. "all" is a sentinel that means no filter.
//
// IMPORTANT: matching is case-insensitive and whitespace-trimmed. If HubSpot
// adds new types we don't recognize, they fall through to the "Other" bucket
// so reps can still see them when "All" is selected.
const COMPANY_TYPE_BUCKETS = [
  {
    id: "all",
    label: "All Types",
    rawTypes: null,  // null = match everything
  },
  {
    id: "mattress",
    label: "Mattress",
    rawTypes: ["gribetz/porter", "gribetz/porter prospect", "aac", "aac prospect"],
  },
  {
    id: "industrial",
    label: "Industrial",
    rawTypes: ["ind customer", "prospect"],
  },
];

function companyMatchesBucket(company, bucketId) {
  if (bucketId === "all") return true;
  const bucket = COMPANY_TYPE_BUCKETS.find(b => b.id === bucketId);
  if (!bucket || !bucket.rawTypes) return true;
  const raw = (company.properties?.type || "").trim().toLowerCase();
  return bucket.rawTypes.includes(raw);
}

// Build a Google Maps-friendly waypoint string for a company.
//
// Strategy: prefer the street address (most precise + always resolvable on
// Google Maps), fall back to city center when no address is on file. We
// deliberately DO NOT include the company name -- Google Maps tries to match
// names as business listings and breaks the entire route when one doesn't
// resolve (e.g. small dealers not in Google's business directory).
//
// Examples:
//   {address:"123 Main St", city:"Tupelo", state:"MS", zip:"38802"}
//     -> "123 Main St, Tupelo, MS 38802"
//
//   {city:"Tupelo", state:"MS", zip:"38802"} (no street address)
//     -> "Tupelo, MS 38802"   (drops to city center, never breaks the link)
//
//   {city:"Tupelo", state:"MS"} (no zip either)
//     -> "Tupelo, MS"
function googleMapsWaypoint(company) {
  const p = company.properties || {};
  const street = (p.address || "").trim();
  const city   = (p.city    || "").trim();
  const state  = (p.state   || "").trim();
  const zip    = (p.zip     || "").trim();

  const cityStateZip = [
    [city, state].filter(Boolean).join(", "),
    zip,
  ].filter(Boolean).join(" ").trim();

  if (street) return street + (cityStateZip ? ", " + cityStateZip : "");
  return cityStateZip || (p.name || "");  // absolute last resort: company name
}

export function RoutePlanner({companies}) {
  // Destination inputs
  const [destCity,  setDestCity]  = useState("Nashville");
  const [destState, setDestState] = useState("TN");
  const [destZip,   setDestZip]   = useState("");
  const [radius,    setRadius]    = useState(100);

  // Company-type filter. Persists across reloads.
  const [typeFilter, setTypeFilter] = useState(() => {
    try { return localStorage.getItem("crm_route_type_filter") || "all"; } catch { return "all"; }
  });
  const setTypeFilterPersist = (id) => {
    setTypeFilter(id);
    try { localStorage.setItem("crm_route_type_filter", id); } catch {}
  };

  // Filter UI state
  const [search, setSearch] = useState("");
  const [sel,    setSel]    = useState([]);

  // Route output state
  const [order,   setOrder]   = useState([]);
  const [miles,   setMiles]   = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(false);

  // Geo database load state
  const [geoReady,   setGeoReady]   = useState(false);
  const [geoLoading, setGeoLoading] = useState(true);

  // Load ZIP database on mount (cached forever after first call)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await loadZipDatabase();
      if (!cancelled) {
        setGeoReady(ok);
        setGeoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve the destination to lat/lng (memoized so it doesn't recompute
  // on every render). Returns null until geoReady AND inputs are valid.
  const destLocation = useMemo(() => {
    if (!geoReady) return null;
    return resolveDestination({
      zip:   destZip.trim(),
      city:  destCity.trim(),
      state: destState.trim(),
    });
  }, [geoReady, destZip, destCity, destState]);

  // Classify every company:
  //   inRadius:  has a known location AND within radius of destination
  //   stateOnly: only knows state; matches destination state but no precise distance
  //   tooFar:    has known location but outside radius
  //   noLocation: no city/state/zip at all -- filtered out entirely
  const {inRadius, stateOnly, tooFarCount, noLocationCount} = useMemo(() => {
    const inR = [], st = [];
    let tooFar = 0, noLoc = 0;
    if (!geoReady) {
      return { inRadius: inR, stateOnly: st, tooFarCount: tooFar, noLocationCount: noLoc };
    }
    const destStateCode = normalizeState(destState);

    for (const c of companies) {
      // Apply company-type filter FIRST. Companies that don't match the
      // selected bucket are entirely excluded from counts and lists.
      if (!companyMatchesBucket(c, typeFilter)) continue;

      const p = c.properties || {};
      const hasCity  = !!(p.city  && p.city.trim());
      const hasState = !!(p.state && p.state.trim());
      const hasZip   = !!(p.zip   && p.zip.trim());

      if (!hasCity && !hasState && !hasZip) { noLoc++; continue; }

      const loc = locationForCompany(c);
      if (loc && destLocation) {
        const d = milesBetween(loc, destLocation);
        if (d <= radius) {
          inR.push({ company: c, distance: d, basis: loc.basis });
        } else {
          tooFar++;
        }
        continue;
      }

      // Fallback: state-only (no city/zip resolution succeeded but state is set)
      if (hasState && destStateCode && stateForCompany(c) === destStateCode) {
        st.push({ company: c });
      } else if (hasState || hasCity || hasZip) {
        // Has some location info but couldn't resolve it (rare -- city not in DB)
        noLoc++;
      }
    }

    // Sort in-radius by distance ascending so closest companies come first
    inR.sort((a, b) => a.distance - b.distance);
    return { inRadius: inR, stateOnly: st, tooFarCount: tooFar, noLocationCount: noLoc };
  }, [companies, destLocation, destState, radius, geoReady, typeFilter]);

  // Apply search filter on top of geo filter
  const filteredInRadius = useMemo(() => {
    if (!search) return inRadius;
    const q = search.toLowerCase();
    return inRadius.filter(({company}) =>
      company.properties.name.toLowerCase().includes(q) ||
      (company.properties.city || "").toLowerCase().includes(q)
    );
  }, [inRadius, search]);

  const filteredStateOnly = useMemo(() => {
    if (!search) return stateOnly;
    const q = search.toLowerCase();
    return stateOnly.filter(({company}) =>
      company.properties.name.toLowerCase().includes(q) ||
      (company.properties.city || "").toLowerCase().includes(q)
    );
  }, [stateOnly, search]);

  // Total visible (for "All / None" buttons)
  const allVisible = [...filteredInRadius, ...filteredStateOnly];

  const toggle = id => setSel(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const selectAll = () => setSel(allVisible.map(x => x.company.id));
  const selectNone = () => setSel([]);

  // AI route planning -- same Claude call as before
  const plan = async () => {
    const tgts = companies.filter(c => sel.includes(c.id));
    if (!tgts.length) return;
    const startingPoint = destZip
      ? destZip + (destCity ? ", " + destCity : "") + (destState ? ", " + destState : "")
      : (destCity + (destState ? ", " + destState : "")).trim() || "Nashville, TN";

    setLoading(true); setOrder([]); setInsight(""); setMiles(null);
    try {
      const stops = tgts.map(c =>
        c.properties.name + " (" +
        [c.properties.city, c.properties.state, c.properties.zip].filter(Boolean).join(", ") + ")"
      );
      // Strict prompt: tell Claude exactly what to return and nothing else.
      // We also defensively extract the JSON object from the response below.
      const prompt = "You are a route-planning assistant. Starting from " + startingPoint +
        ", produce the most efficient driving order through these stops:\n\n" +
        stops.map((s, i) => (i + 1) + ". " + s).join("\n") +
        "\n\nRespond with ONLY a single JSON object, no preamble, no markdown, no explanation outside the JSON. Format exactly:\n" +
        '{"order":[1,3,2],"miles":425,"insight":"Brief 1-sentence note about the route logic."}';
      const r = await aiCall(prompt);
      // Claude API returns {content: [{type:"text", text:"..."}], ...}
      const text = r?.content?.find(b => b.type === "text")?.text || "";
      if (!text) throw new Error("No response from AI");
      // Defensive extraction: find the first {...} JSON block in the response
      // even if Claude added a "Looking at these stops..." preamble.
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("AI did not return JSON");
      const cleaned = jsonMatch[0].replace(/```json\s*|\s*```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      setOrder(parsed.order || []);
      setMiles(parsed.miles || null);
      setInsight(parsed.insight || "");
    } catch (e) {
      setInsight("Could not plan route: " + e.message);
    }
    setLoading(false);
  };

  const orderedTargets = order.length
    ? order.map(i => companies.find(c => c.id === sel[i - 1])).filter(Boolean)
    : [];

  // Format distance for display
  const fmtMi = mi => mi < 10 ? mi.toFixed(1) + " mi" : Math.round(mi) + " mi";

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      {/* Destination card */}
      <div style={{background:"#fff",borderRadius:14,padding:18,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",marginBottom:14}}>
        <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:15,fontWeight:600,color:"var(--dark)",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          📍 Destination
          {geoLoading && <span style={{fontFamily:"Barlow,sans-serif",fontSize:11,fontWeight:400,fontStyle:"italic",color:"var(--muted)"}}><Spin/> loading ZIP database...</span>}
          {!geoLoading && !geoReady && <span style={{fontFamily:"Barlow,sans-serif",fontSize:11,fontWeight:400,fontStyle:"italic",color:"var(--red)"}}>ZIP database unavailable -- falling back to state matching only</span>}
        </div>
        <div className="route-dest" style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:10,marginBottom:0}}>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>City</label>
            <input value={destCity} onChange={e => setDestCity(e.target.value)} placeholder="Nashville"
              style={{padding:"9px 12px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>State</label>
            <select value={destState} onChange={e => setDestState(e.target.value)}
              style={{padding:"9px 12px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif",cursor:"pointer"}}>
              <option value="">-</option>
              {US_STATES.map(([code, name]) => (
                <option key={code} value={code}>{code} - {name}</option>
              ))}
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>ZIP Code</label>
            <input value={destZip} onChange={e => setDestZip(e.target.value.replace(/[^\d-]/g, "").slice(0, 10))} placeholder="37027"
              style={{padding:"9px 12px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Radius</label>
            <select value={radius} onChange={e => setRadius(parseInt(e.target.value))}
              style={{padding:"9px 12px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif",cursor:"pointer",fontWeight:600,color:"var(--brown)"}}>
              {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} miles</option>)}
            </select>
          </div>
        </div>

        {/* Resolution status */}
        {geoReady && (
          <div style={{marginTop:12,padding:"8px 12px",borderRadius:8,background:"var(--warm1)",fontSize:12,color:"var(--muted)",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {destLocation ? (
              <>
                <span>📍 Centered on</span>
                <strong style={{color:"var(--brown)"}}>
                  {destLocation.resolution === "zip" ? "ZIP " + destZip
                    : destLocation.resolution === "city" ? destCity + ", " + normalizeState(destState)
                    : "State " + normalizeState(destState) + " (averaged)"}
                </strong>
                <span>·</span>
                <strong style={{color:"var(--brown)"}}>{radius}-mile radius</strong>
                <span>·</span>
                <strong style={{color:"var(--green)"}}>{inRadius.length}</strong>
                <span>in radius</span>
                {stateOnly.length > 0 && (
                  <>
                    <span>·</span>
                    <strong style={{color:"var(--brown)"}}>{stateOnly.length}</strong>
                    <span>state-only (approximate)</span>
                  </>
                )}
                {tooFarCount > 0 && (
                  <>
                    <span>·</span>
                    <span style={{color:"var(--warm3)"}}>{tooFarCount} too far</span>
                  </>
                )}
                {noLocationCount > 0 && (
                  <>
                    <span>·</span>
                    <span style={{color:"var(--warm3)"}}>{noLocationCount} no location</span>
                  </>
                )}
              </>
            ) : (
              <span style={{color:"var(--red)"}}>⚠ Enter a valid city/state or ZIP code</span>
            )}
          </div>
        )}
      </div>

      {/* Company-type filter -- restricts which companies are considered for
          this route plan. Counts in the dropdown reflect total available per
          type (not narrowed by current radius). */}
      <div style={{background:"#fff",borderRadius:14,padding:"12px 18px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",marginBottom:14,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <span style={{fontSize:13,fontWeight:600,color:"var(--brown)",fontFamily:"Barlow Condensed,sans-serif"}}>🏷 Show</span>
        <select value={typeFilter} onChange={e => setTypeFilterPersist(e.target.value)}
          style={{padding:"7px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,fontFamily:"Barlow,sans-serif",fontWeight:600,color:"var(--dark)",cursor:"pointer",outline:"none",minWidth:180}}>
          {COMPANY_TYPE_BUCKETS.map(b => {
            const count = b.id === "all"
              ? companies.length
              : companies.filter(c => companyMatchesBucket(c, b.id)).length;
            return <option key={b.id} value={b.id}>{b.label} ({count})</option>;
          })}
        </select>
        {typeFilter !== "all" && (
          <span style={{fontSize:11,fontStyle:"italic",color:"var(--muted)"}}>
            Filtered to {COMPANY_TYPE_BUCKETS.find(b => b.id === typeFilter)?.label || "?"} companies
          </span>
        )}
      </div>

      {/* Two-column layout: company list + map/results */}
      <div className="g2" style={{display:"grid",gridTemplateColumns:"minmax(0, 1fr) minmax(0, 1fr)",gap:14,alignItems:"start"}}>
        {/* Left: filter + stop list */}
        <div style={{background:"#fff",borderRadius:14,padding:16,boxShadow:"var(--sh)",border:"1px solid var(--warm2)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:15,fontWeight:600,color:"var(--dark)"}}>🏢 Select Stops</div>
            <div style={{display:"flex",gap:5}}>
              <button onClick={selectAll} disabled={allVisible.length === 0}
                style={{padding:"4px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:allVisible.length?"var(--warm1)":"#fafafa",fontSize:11,color:"var(--brown)",fontWeight:600,cursor:allVisible.length?"pointer":"not-allowed",fontFamily:"Barlow,sans-serif"}}>All</button>
              <button onClick={selectNone} disabled={sel.length === 0}
                style={{padding:"4px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:sel.length?"var(--warm1)":"#fafafa",fontSize:11,color:"var(--brown)",fontWeight:600,cursor:sel.length?"pointer":"not-allowed",fontFamily:"Barlow,sans-serif"}}>None</button>
            </div>
          </div>
          <FI placeholder="🔍 Filter..." value={search} onChange={setSearch}/>

          <div style={{marginTop:10,maxHeight:520,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
            {/* In-radius companies, sorted by distance */}
            {filteredInRadius.length > 0 && (
              <>
                <div style={{fontSize:10,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,padding:"6px 4px 4px"}}>
                  Within {radius} miles ({filteredInRadius.length})
                </div>
                {filteredInRadius.map(({company: c, distance, basis}) => (
                  <label key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:sel.includes(c.id)?"var(--warm1)":"#fff",border:"1px solid "+(sel.includes(c.id)?"var(--brown)":"var(--warm2)"),cursor:"pointer",transition:"background .12s"}}>
                    <input type="checkbox" checked={sel.includes(c.id)} onChange={()=>toggle(c.id)} style={{accentColor:"var(--brown)",cursor:"pointer"}}/>
                    <div style={{flex:1,minWidth:0,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{fontWeight:500,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.properties.name}</div>
                        <div style={{fontSize:10,color:"var(--muted)"}}>
                          {[c.properties.city, c.properties.state].filter(Boolean).join(", ")}
                          {basis === "city" && <span style={{marginLeft:4,fontStyle:"italic"}}>(city center)</span>}
                        </div>
                      </div>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:99,background:"var(--warm2)",color:"var(--brown)",fontWeight:600,whiteSpace:"nowrap",flexShrink:0}}>
                        {fmtMi(distance)}
                      </span>
                    </div>
                  </label>
                ))}
              </>
            )}

            {/* State-only fallback companies */}
            {filteredStateOnly.length > 0 && (
              <>
                <div style={{fontSize:10,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,padding:"12px 4px 4px"}}>
                  Same state, location approximate ({filteredStateOnly.length})
                </div>
                {filteredStateOnly.map(({company: c}) => (
                  <label key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:sel.includes(c.id)?"var(--warm1)":"#fff",border:"1px dashed "+(sel.includes(c.id)?"var(--brown)":"var(--warm2)"),cursor:"pointer",transition:"background .12s"}}>
                    <input type="checkbox" checked={sel.includes(c.id)} onChange={()=>toggle(c.id)} style={{accentColor:"var(--brown)",cursor:"pointer"}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:500,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.properties.name}</div>
                      <div style={{fontSize:10,color:"var(--muted)",fontStyle:"italic"}}>
                        {c.properties.state || "?"} only — no city/ZIP
                      </div>
                    </div>
                  </label>
                ))}
              </>
            )}

            {!geoLoading && allVisible.length === 0 && (
              <div style={{padding:30,textAlign:"center",color:"var(--muted)",fontSize:12,fontStyle:"italic"}}>
                {destLocation
                  ? "No companies within " + radius + " miles of " + (destCity || destZip || normalizeState(destState))
                  : "Enter a destination above to see nearby companies"}
              </div>
            )}
          </div>
        </div>

        {/* Right: coverage map / route output */}
        <div style={{background:"#fff",borderRadius:14,padding:16,boxShadow:"var(--sh)",border:"1px solid var(--warm2)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:15,fontWeight:600,color:"var(--dark)"}}>
              {order.length ? "🗺 Optimized Route" : "📋 Selected Stops"}
            </div>
            <span style={{fontSize:11,color:"var(--muted)"}}>{sel.length} of {allVisible.length} selected</span>
          </div>

          {sel.length === 0 ? (
            <div style={{padding:"60px 20px",textAlign:"center",color:"var(--muted)"}}>
              <div style={{fontSize:32,marginBottom:10,opacity:.3}}>🗺</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--dark)",marginBottom:6}}>Select stops and click Plan Best Route</div>
              <div style={{fontSize:12,lineHeight:1.6}}>AI will calculate the most efficient driving order<br/>Route opens in Google Maps</div>
            </div>
          ) : (
            <>
              {order.length === 0 ? (
                <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:5,marginBottom:14}}>
                  {sel.map(id => {
                    const c = companies.find(c => c.id === id);
                    if (!c) return null;
                    return (
                      <div key={id} style={{padding:"7px 10px",background:"var(--warm1)",borderRadius:8,fontSize:12,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                        <span style={{flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.properties.name}</span>
                        <span style={{fontSize:10,color:"var(--muted)",flexShrink:0}}>{c.properties.city || c.properties.state}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:5,marginBottom:14}}>
                  {orderedTargets.map((c, i) => (
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"var(--warm1)",borderRadius:10}}>
                      <div style={{width:24,height:24,borderRadius:99,background:"var(--brown)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{i + 1}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.properties.name}</div>
                        <div style={{fontSize:10,color:"var(--muted)"}}>{[c.properties.city, c.properties.state].filter(Boolean).join(", ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {miles !== null && (
                <div style={{padding:"8px 12px",background:"#e6f4ed",borderRadius:8,fontSize:12,marginBottom:10,color:"var(--green)",fontWeight:600,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>≈ {miles} total miles</span>
                </div>
              )}
              {insight && (
                <div style={{padding:"8px 12px",background:"var(--warm1)",borderRadius:8,fontSize:11,color:"var(--muted)",fontStyle:"italic",marginBottom:10}}>
                  💡 {insight}
                </div>
              )}

              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Btn onClick={plan} disabled={loading || sel.length === 0}>
                  {loading ? <><Spin/> Planning...</> : order.length ? "Re-plan" : "✨ Plan Best Route"}
                </Btn>
                {order.length > 0 && (
                  <a target="_blank" rel="noreferrer"
                    href={
                      "https://www.google.com/maps/dir/" +
                      encodeURIComponent((destZip || destCity + ", " + destState).trim()) +
                      "/" +
                      orderedTargets.map(c => encodeURIComponent(googleMapsWaypoint(c))).join("/")
                    }
                    style={{padding:"10px 18px",borderRadius:10,background:"var(--green)",color:"#fff",fontSize:13,fontWeight:600,textDecoration:"none",fontFamily:"Barlow,sans-serif",display:"inline-flex",alignItems:"center",gap:6}}>
                    🗺 Open in Google Maps
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
